import styled from 'styled-components';

import { useClickAttachment } from 'hooks/useClickAttachment';
import colors from 'styles/theme/colors';
import textStyles from 'styles/theme/textStyles';

export type AdmissionsCriteriaColor = 'green' | 'yellow' | 'red' | 'orange' | 'blue';

export type AdmissionSource = {
  attachmentId: string;
  displayId: number;
};

export type AdmissionsCriterion = {
  title: string;
  description?: string;
  sourceCount: number;
  sources?: AdmissionSource[];
};

export type AdmissionsCriteriaProps = {
  categoryLabel: string;
  color: AdmissionsCriteriaColor;
  criteria: AdmissionsCriterion[];
};

export const colorConfig: Record<AdmissionsCriteriaColor, { accent: string; background: string }> = {
  green: {
    accent: colors.green100,
    background: colors.green5,
  },
  yellow: {
    accent: colors.yellow100,
    background: colors.yellow5,
  },
  red: {
    accent: colors.red100,
    background: colors.red5,
  },
  orange: {
    accent: colors.orange100,
    background: colors.orange5,
  },
  blue: {
    accent: colors.blue100,
    background: colors.blue5,
  },
};

export default function AdmissionsCriteria({ categoryLabel, color, criteria }: AdmissionsCriteriaProps) {
  const { accent, background } = colorConfig[color];

  return (
    <Card>
      <ColorBar $accent={accent} />
      <Header $background={background}>
        <HeaderText>
          {criteria.length} {categoryLabel} Criteria Found
        </HeaderText>
      </Header>
      {criteria.map((criterion, index) => (
        <div key={index}>
          {index > 0 && <Divider />}
          <CriteriaItem>
            <TitleRow>
              <Title>{criterion.title}</Title>
              {criterion.sources && criterion.sources.length > 0 && (
                <SourceBadges>
                  {criterion.sources.map((source) => (
                    <ClickableSourceBadge key={source.displayId} source={source} />
                  ))}
                </SourceBadges>
              )}
            </TitleRow>
            {criterion.description && <Description>{criterion.description}</Description>}
          </CriteriaItem>
        </div>
      ))}
    </Card>
  );
}

function ClickableSourceBadge({ source }: { source: AdmissionSource }) {
  const openAttachment = useClickAttachment(source.attachmentId, 'Review::Admission');

  return <SourceBadge onClick={openAttachment}>{source.displayId}</SourceBadge>;
}

const SourceBadge = styled.span`
  min-width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 500;
  cursor: pointer;
  background: ${colors.white};
  color: ${colors.black100};
  border: 1px solid ${colors.black25};
  border-radius: 16px;
  padding: 0 2px;

  &:hover {
    background: ${colors.blue5};
    border-color: ${colors.blue100};
  }
`;

const Card = styled.div`
  position: relative;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  overflow: hidden;
`;

const ColorBar = styled.div<{ $accent: string }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: ${({ $accent }) => $accent};
  border-radius: 4px 0 0 4px;
`;

const Header = styled.div<{ $background: string }>`
  background: ${({ $background }) => $background};
  padding: 16px 16px 16px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderText = styled.p`
  margin: 0;
  font-family: ${textStyles.base.fontFamily};
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
`;

const CriteriaItem = styled.div`
  padding: 16px 16px 16px 24px;
`;

const Divider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${colors.black10};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Title = styled.p`
  margin: 0;
  font-family: ${textStyles.base.fontFamily};
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
`;

const SourceBadges = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
`;

const Description = styled.p`
  margin: 8px 0 0;
  font-family: ${textStyles.base.fontFamily};
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: ${colors.black75};
`;
